<template>
    <div>
      <div class="hw-content">
        <div class="panel">
          <div class="notes">
            <div>
                <div v-for="(c,index) in classes" :key="'hw-'+index">
                  <div @click.stop="selectClass(c)" class="class-row" :class="{ active: selectedClass?.id === c.id }">
                    <div class="class-icon">
                      <i class="fas fa-book"></i>
                    </div>
                    <span>{{ c.name }}</span>
                    
                  </div>
                </div>
            </div>
          </div>
        </div>

        <div class="content-area">
          <div v-if="!selectedClass" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-book-open"></i>
            </div>
            <h3>Select a Lesson</h3>
            <p>Choose a lesson from the left list to view homework questions</p>
          </div>

          <div v-else class="course-content">
          <div class="course-header">
            <div class="course-title">
              <i class="fas fa-book"></i>
              <span>{{ selectedClass.name }}</span>
            </div>
            <div class="header-actions">
              <div class="wss-status" :class="connected ? 'connected' : 'disconnected'">
                <span class="status-dot"></span>
                <span>{{ connected ? '已连接' : '未连接' }}</span>
              </div>
              <button class="save-btn" @click="saveToServer" :disabled="saving">
                <i class="fas" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                Send To Peer
              </button>
              <button class="save-btn" @click="resetHomework">
                <i class="fas fa-redo"></i>
                Reset
              </button>
            </div>
          </div>

            <div class="tabs-container">
              <div class="tabs">
                <button 
                  v-for="tab in tabs" 
                  :key="tab.id"
                  @click="activeTab = tab.id"
                  class="tab"
                  :class="{ active: activeTab === tab.id }"
                >
                  <i :class="tab.icon"></i>
                  <span>{{ tab.name }}</span>
                </button>
              </div>
            </div>

            <div class="tab-content">

              <div v-if="activeTab === 'choice'" class="choice-section">
                <div class="section-header">
                  <h4>Multiple Choice</h4>
                  <span class="question-count">{{ choiceQuestions.length }} questions</span>
                </div>
                <div class="question-list">
                  <div v-for="(q, index) in choiceQuestions" :key="'cq-'+index" class="question-item">
                    <div class="question-number">{{ index + 1 }}</div>
                    <div class="question-content">
                      <div class="question-text">{{ q.question }}</div>
                      <div class="options">
                        <div 
                          v-for="(option, oIndex) in q.options" 
                          :key="'opt-'+index+'-'+oIndex"
                          @click="selectChoice(index, oIndex)"
                          class="option"
                          :class="{ selected: choiceAnswers[index] === oIndex, correct: showAnswer && oIndex === q.answer, wrong: showAnswer && choiceAnswers[index] === oIndex && oIndex !== q.answer }"
                        >
                          <span class="option-label">{{ getOptionLabel(oIndex) }}</span>
                          <span class="option-text">{{ option }}</span>
                          <i v-if="showAnswer && oIndex === q.answer" class="fas fa-check correct-icon"></i>
                          <i v-if="showAnswer && choiceAnswers[index] === oIndex && oIndex !== q.answer" class="fas fa-times wrong-icon"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="choice-actions">
                  <button @click="showAnswer = !showAnswer" class="action-btn">
                    {{ showAnswer ? 'Hide Answers' : 'Show Answers' }}
                  </button>
                  <button @click="submitChoice" class="action-btn primary">
                    Submit Answers
                  </button>
                </div>
              </div>

              <div v-if="activeTab === 'programming'" class="programming-section">
                <div class="section-header">
                  <h4>Programming Exercises</h4>
                  <div class="header-actions">
                    <button @click="saveCode" class="action-btn">
                      <i class="fas fa-download"></i> Save Code
                    </button>
                  </div>
                </div>
                
                <div class="programming-content">
                  <div class="question-panel">
                    <div class="question-title">Questions</div>
                    <div class="question-body">
                      <div v-for="(q, index) in programmingQuestions" :key="'pq-'+index" 
                           @click="selectProgramming(index)"
                           class="programming-question"
                           :class="{ active: selectedProgrammingIndex === index }">
                        <div class="question-header">
                          <span class="question-num">{{ index + 1 }}.</span>
                          <span class="question-label">{{ q.title }}</span>
                        </div>
                        <div class="question-desc">{{ q.description }}</div>
                        <div v-if="q.example" class="question-example">
                          <div class="example-label">Example:</div>
                          <div class="example-content">{{ q.example }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="editor-panel">
                    <div class="editor-header">
                      <span>Code Editor</span>
                      <button @click="runCode" class="run-btn" :disabled="running">
                        <i class="fas fa-play"></i>
                        {{ running ? 'Running...' : 'Run Code' }}
                      </button>
                    </div>
                    <editor 
                      v-model="code" 
                      lang="java" 
                      theme="monokai"  
                      class="editor editor-font-2" 
                      ref="aceEditor" 
                      id="codeEditor" 
                      :options="editorOptions"
                      @input="onCodeChange"
                    ></editor>

                    <div class="stdin-input">
                      <i class="fas fa-terminal"></i>
                      <input v-model="stdin" placeholder="Enter standard input..."/>
                    </div>

                    <div v-if="result" class="result-panel">
                      <div class="result-header">
                        <span>Output</span>
                        <button class="close-btn" @click="result = null">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                      <div class="result-content" v-html="result"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
import Vue from 'vue';
import editor from 'vue2-ace-editor'
import util from '../utils/util';
  require('brace/mode/java');
  require('brace/theme/chrome');
  
  export default {
    data() {
      return {
        code: "",
        classes:[],
        font:2,
        tab:"basic",
        result :null,
        stdin:"",
        running:false,
        editorOptions: {
          enableBasicAutocompletion: false,
          enableLiveAutocompletion: false,
          enableSnippets: false,
          enableLinking: false,
          foldStyle: 'manual',
        },
        userInfo:null,
        selectedClass: null,
        activeTab: 'choice',
        tabs: [
          { id: 'choice', name: 'Multiple Choice', icon: 'fas fa-check-circle' },
          { id: 'programming', name: 'Programming', icon: 'fas fa-code' }
        ],
        thinkingQuestions: [],
        thinkingAnswers: [],
        choiceQuestions: [],
        choiceAnswers: [],
        showAnswer: false,
        programmingQuestions: [],
        selectedProgrammingIndex: 0,
        saveTimer: null,
        saving: false,
        // WebSocket
        socket: null,
        connected: false,
        loopCheck: true,
        pendingHomeworkData: null,
      };
    },
    components:{
      editor
    },
    mounted() {
      var user = localStorage.getItem('user');
          if(user && user != null && user != ''){
              var userObj = JSON.parse(user);
              this.userInfo = userObj;
          }
          else{
              location.href = '/';
              return;
          }
        this.loadList();
        this.connectLoop();
    },
    beforeDestroy() {
      this.connected = false;
      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
      }
    },
    methods: {
      connectLoop() {
        var mobile = this.userInfo.Mobile;
        if (!mobile || mobile === '') {
          mobile = 's1';
        }
        if (!this.connected) {
          this.connectServer();
        }
        var interval = setInterval(() => {
          if (!this.connected) {
            this.connectServer();
          } else {
            clearInterval(interval);
          }
        }, 5000);
      },
      connectServer() {
        var mobile = this.userInfo.Mobile;
        if (!mobile || mobile === '') {
          mobile = 's1';
        }


        var mobile = this.userInfo.Mobile;
      var target = 'patrick';

      if(util.getSearchString('mobile') != null){
        mobile = util.getSearchString('mobile');
      }
      if(util.getSearchString('target')!= null){
        target = util.getSearchString('target');
      }

        const socket = new WebSocket('wss://wss.dctechonline.cn:8024');
        this.socket = socket;

        socket.addEventListener('open', () => {
          console.log('WebSocket connected for homework');

          socket.send(JSON.stringify({
            type: 'user',
            mobile: mobile,
            target: target,
            classType: 'homework',
          }));

          this.connected = true;

          // Heartbeat loop
          var interval2 = setInterval(() => {
            if (this.loopCheck === false) {
              clearInterval(interval2);
              this.connected = false;
              this.connectLoop();
              return;
            } else {
              socket.send(JSON.stringify({
                type: 'loopcheck',
                mobile: mobile,
              }));
              this.loopCheck = false;
            }
          }, 20000);
        });

        socket.addEventListener('message', event => {
          const reader = new FileReader();
          reader.addEventListener('loadend', () => {
            const text = reader.result;
            const data = JSON.parse(text);
            if (data.type === 'loopcheck') {
              this.loopCheck = true;
            }
            if (data.type === 'aphomework') {
              console.log('Received aphomework:', data);
              this.applyHomeworkData(data.data);
            }
          });
          reader.readAsText(event.data);
        });

        socket.addEventListener('close', () => {
          this.connected = false;
        });

        socket.addEventListener('error', () => {
          this.connected = false;
        });
      },
      getOptionLabel(index) {
        return String.fromCharCode(65 + index);
      },
      selectClass(c) {
        console.log('Selected class:', c);
        this.selectedClass = c;
        this.loadHomeworkData(c);
      },
      loadHomeworkData(c) {
        this.thinkingQuestions = [];
        this.thinkingAnswers = [];
        this.choiceQuestions = [];
        this.choiceAnswers = [];
        this.programmingQuestions = [];
        this.code = "";
        this.showAnswer = false;
        this.selectedProgrammingIndex = 0;
        this.result = null;

        const path = `/data/homework/hw${c.id}.json?random=${new Date().getTime()}`;
        console.log('Loading homework data from:', path);
        this.$http.get(path).then(response => {
          console.log('Response data:', response.data);
          const data = response.data;
          
          this.thinkingQuestions = data.thinking || [];
          this.thinkingAnswers = Array.from({ length: this.thinkingQuestions.length }, () => '');
          
          this.choiceQuestions = data.choice || [];
          this.choiceAnswers = Array.from({ length: this.choiceQuestions.length }, () => -1);
          
          this.programmingQuestions = data.programming || [];
          if (this.programmingQuestions.length > 0 && this.programmingQuestions[0].template) {
            this.code = this.programmingQuestions[0].template;
          }
          console.log('Loaded:', this.thinkingQuestions.length, 'thinking,', this.choiceQuestions.length, 'choice,', this.programmingQuestions.length, 'programming');
          
          this.$nextTick(() => {
            this.loadSavedHomework();
            if (this.pendingHomeworkData) {
              this.pendingHomeworkData();
            }
          });
        }).catch(error => {
          console.error('Error loading homework data:', error);
        });
      },
      loadThinking(path) {
      },
      loadChoice(path) {
      },
      loadProgramming(path) {
      },
      onCodeChange() {
        this.autoSave();
      },
      onThinkingChange(index) {
        this.autoSave();
      },
      selectChoice(qIndex, oIndex) {
        Vue.set(this.choiceAnswers, qIndex, oIndex);
        this.autoSave();
      },
      selectProgramming(index) {
        this.saveCurrentCode();
        this.selectedProgrammingIndex = index;
        const q = this.programmingQuestions[index];
        if (q && q.template) {
          this.code = this.getSavedCode(index) || q.template;
        }
        this.autoSave();
      },
      getStorageKey() {
        if (!this.selectedClass || !this.userInfo) return null;
        return `ap_homework_${this.userInfo.UserId}_${this.selectedClass.id}`;
      },
      saveHomework() {
        const key = this.getStorageKey();
        if (!key) return;
        
        const data = {
          thinkingAnswers: this.thinkingAnswers,
          choiceAnswers: this.choiceAnswers,
          programmingCodes: this.getProgrammingCodes(),
          selectedProgrammingIndex: this.selectedProgrammingIndex,
          savedAt: new Date().toISOString()
        };
        
        localStorage.setItem(key, JSON.stringify(data));
        console.log('Homework saved to localStorage:', key);
      },
      resetHomework() {
        if (!confirm('Are you sure you want to reset all progress for this lesson?')) {
          return;
        }
        
        const key = this.getStorageKey();
        if (key) {
          localStorage.removeItem(key);
          console.log('Homework cleared from localStorage:', key);
        }
        
        this.thinkingAnswers = Array.from({ length: this.thinkingQuestions.length }, () => '');
        this.choiceAnswers = Array.from({ length: this.choiceQuestions.length }, () => -1);
        this.selectedProgrammingIndex = 0;
        this.showAnswer = false;
        this.result = null;
        
        if (this.programmingQuestions.length > 0 && this.programmingQuestions[0].template) {
          this.code = this.programmingQuestions[0].template;
        }
        
        alert('Progress reset successfully!');
      },
      loadSavedHomework() {
        const key = this.getStorageKey();
        if (!key) return;
        
        const saved = localStorage.getItem(key);
        if (!saved) return;
        
        try {
          const data = JSON.parse(saved);
          
          if (data.thinkingAnswers && data.thinkingAnswers.length === this.thinkingQuestions.length) {
            this.thinkingAnswers = data.thinkingAnswers;
          }
          
          if (data.choiceAnswers && data.choiceAnswers.length === this.choiceQuestions.length) {
            this.choiceAnswers = data.choiceAnswers;
          }
          
          if (data.selectedProgrammingIndex !== undefined) {
            this.selectedProgrammingIndex = data.selectedProgrammingIndex;
          }
          
          if (data.programmingCodes && this.programmingQuestions.length > 0) {
            const q = this.programmingQuestions[this.selectedProgrammingIndex];
            if (q && q.template) {
              this.code = data.programmingCodes[this.selectedProgrammingIndex] || q.template;
            }
          }
          
          console.log('Homework loaded from localStorage:', key);
        } catch (e) {
          console.error('Failed to load saved homework:', e);
        }
      },
      autoSave() {
        if (this.saveTimer) {
          clearTimeout(this.saveTimer);
        }
        this.saveTimer = setTimeout(() => {
          this.saveHomework();
        }, 1000);
      },
      saveCurrentCode() {
        const key = this.getStorageKey();
        if (!key) return;
        
        const saved = localStorage.getItem(key);
        let data = saved ? JSON.parse(saved) : {};
        
        if (!data.programmingCodes) {
          data.programmingCodes = [];
        }
        data.programmingCodes[this.selectedProgrammingIndex] = this.code;
        
        localStorage.setItem(key, JSON.stringify(data));
      },
      getSavedCode(index) {
        const key = this.getStorageKey();
        if (!key) return null;
        
        const saved = localStorage.getItem(key);
        if (!saved) return null;
        
        try {
          const data = JSON.parse(saved);
          return data.programmingCodes && data.programmingCodes[index] ? data.programmingCodes[index] : null;
        } catch (e) {
          return null;
        }
      },
      applyHomeworkData(hwData) {
        if (!hwData) return;
        console.log('applyHomeworkData:', hwData);
        
        const apply = () => {
          // Update thinking answers
          if (hwData.thinkingAnswers) {
            hwData.thinkingAnswers.forEach((val, i) => {
              Vue.set(this.thinkingAnswers, i, val);
            });
          }
          // Update choice answers
          if (hwData.choiceAnswers) {
            hwData.choiceAnswers.forEach((val, i) => {
              Vue.set(this.choiceAnswers, i, val);
            });
          }
          // Update programming codes
          if (hwData.programmingCodes) {
            hwData.programmingCodes.forEach((code, i) => {
              if (i === (hwData.selectedProgrammingIndex != null ? hwData.selectedProgrammingIndex : this.selectedProgrammingIndex)) {
                this.code = code;
              }
              const key = this.getStorageKey();
              if (key) {
                const saved = localStorage.getItem(key);
                let localData = saved ? JSON.parse(saved) : {};
                if (!localData.programmingCodes) {
                  localData.programmingCodes = [];
                }
                localData.programmingCodes[i] = code;
                localStorage.setItem(key, JSON.stringify(localData));
              }
            });
          }
          if (hwData.selectedProgrammingIndex != null) {
            this.selectedProgrammingIndex = hwData.selectedProgrammingIndex;
          }
          this.autoSave();
          this.pendingHomeworkData = null;
        };
        
        // Auto-select the correct class if needed
        if (hwData.classId && (!this.selectedClass || this.selectedClass.id !== hwData.classId)) {
          const targetClass = this.classes.find(c => c.id === hwData.classId);
          if (targetClass) {
            console.log('Auto-switching to class:', targetClass.name);
            this.pendingHomeworkData = apply;
            this.selectClass(targetClass);
            return;
          }
        }
        
        apply();
      },
      getProgrammingCodes() {
        const codes = [];
        this.programmingQuestions.forEach((q, index) => {
          codes[index] = index === this.selectedProgrammingIndex ? this.code : this.getSavedCode(index);
        });
        return codes;
      },
      submitChoice() {
        let correct = 0;
        let total = this.choiceQuestions.length;
        
        this.choiceAnswers.forEach((ans, idx) => {
          if (ans === this.choiceQuestions[idx].answer) {
            correct++;
          }
        });

        this.showAnswer = true;
        alert(`Answers submitted!\nCorrect: ${correct} / ${total}\nAccuracy: ${Math.round(correct / total * 100)}%`);
      },
      saveCode() {
        if(this.code.trim() == ''){
          alert('No code to save')
          return;
        }
        const blob = new Blob([this.code], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'homework.java';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      runCode() {
        if(this.code == ''){
          return;
        }

        if(this.running ){
          return;
        }

        this.running = true;
        this.result = null;

        var postInfo = {};
        postInfo.code = encodeURIComponent(this.code);
        postInfo.language = 7;
        postInfo.stdin = this.stdin;
        postInfo.fileext = "java";

        this.$http.post("https://aiapi.dctechonline.cn/api/runcode",postInfo).then(response => {
          this.running = false;
          this.result = response.data.output != '' ? response.data.output.replace(/\n/g, '<br>') : response.data.errors.replace(/\n/g, '<br>')
        });
      },
      saveToServer() {
        if (this.saving) return;
        if (!this.connected || !this.socket) {
          alert('WebSocket 未连接，请稍后重试');
          return;
        }
        this.saving = true;

        var target = "patrick";

        if(util.getSearchString("target") != null){
          target = util.getSearchString("target");
        }

        const payload = {
          type: 'aphomework',
          mobile: this.userInfo.Mobile || 's1',
          target: target,
          data: {
            userId: this.userInfo.UserId,
            classId: this.selectedClass.id,
            className: this.selectedClass.name,
            thinkingAnswers: this.thinkingAnswers,
            choiceAnswers: this.choiceAnswers,
            programmingCodes: this.getProgrammingCodes(),
            selectedProgrammingIndex: this.selectedProgrammingIndex,
            savedAt: new Date().toISOString()
          }
        };

        try {
          this.socket.send(JSON.stringify(payload));
          this.saving = false;
          //alert('保存成功');
        } catch (err) {
          this.saving = false;
          console.error('WebSocket send error:', err);
         // alert('保存失败，请检查网络后重试');
        }
      },
      loadList(){
        var r = new Date().getTime();
        var index = util.getSearchString("id");
        this.$http.get("/data/homework/ap"+index+".json?random="+r).then(response => {
          this.classes = response.data;
        });
      },
    },
  };
  </script>
  
  <style lang="scss">
  @import '@/styles/variables.scss';
  
  .hw-content{
    position: fixed;
    top:56px;
    left:0px;
    width:100%;
    height:calc(100% - 56px);
    display: flex;
    background: $bg-primary;

    .panel{
      width: 280px;
      border-left:none !important;
      
      .notes{
        margin:12px;
        margin-top:35px;
        height:calc(100% - 50px);
        background: white;
        padding: 14px;
        border-radius: 12px;
        box-shadow: $shadow-card;
        border: 1px solid $border-light;
        overflow: auto;
        
        @include scrollbar-custom;
      }
    }

    .content-area{
      flex: 1;
      height: 100%;
      padding: 12px;
      overflow: auto;

      .empty-state{
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: white;
        border-radius: 12px;
        box-shadow: $shadow-card;
        border: 1px solid $border-light;

        .empty-icon{
          width: 80px;
          height: 80px;
          background: rgba($primary-color, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          font-size: 32px;
          color: $primary-color;
        }

        h3{
          font-size: 18px;
          color: $text-primary;
          margin-bottom: 8px;
        }

        p{
          font-size: 14px;
          color: $text-muted;
        }
      }

      .course-content{
        background: white;
        border-radius: 12px;
        box-shadow: $shadow-card;
        border: 1px solid $border-light;
        overflow: hidden;
        height: calc(100% - 20px);
        margin-top: 20px;
        display: flex;
        flex-direction: column;

        .course-header{
          padding: 16px 20px;
          background: $primary-gradient;
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .course-title{
            display: flex;
            align-items: center;
            gap: 10px;
            font-weight: 600;
            font-size: 16px;
          }

          .save-btn{
            padding: 8px 18px;
            background: white;
            color: $primary-color;
            border: 1.5px solid $primary-color;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: all 0.2s;
            white-space: nowrap;

            &:hover:not(:disabled) {
              background: $primary-color;
              color: white;
              box-shadow: 0 4px 12px rgba($primary-color, 0.3);
            }

            &:disabled {
              opacity: 0.7;
              cursor: not-allowed;
            }
          }

          .header-actions {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .wss-status {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
            white-space: nowrap;

            .status-dot {
              width: 8px;
              height: 8px;
              border-radius: 50%;
            }

            &.connected {
              background: rgba(255,255,255,0.15);
              .status-dot { background: #4ade80; }
            }

            &.disconnected {
              background: rgba(255,255,255,0.1);
              opacity: 0.7;
              .status-dot { background: #f87171; }
            }
          }
        }

        .tabs-container{
          border-bottom: 1px solid $border-light;
          padding: 0 20px;

          .tabs{
            display: flex;
            gap: 0;

            .tab{
              padding: 12px 20px;
              background: none;
              border: none;
              font-size: 14px;
              font-weight: 500;
              color: $text-secondary;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 8px;
              border-bottom: 2px solid transparent;
              transition: all 0.2s ease;

              &:hover{
                color: $primary-color;
              }

              &.active{
                color: $primary-color;
                border-bottom-color: $primary-color;
                font-weight: 600;
              }
            }
          }
        }

        .tab-content{
          flex: 1;
          overflow: auto;
          padding: 20px;
        }
      }
    }
  }

  .class-row{
    padding: 10px 12px;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    border-radius: 8px;
    transition: all 0.2s ease;
    margin-bottom: 4px;
    
    .class-icon {
      width: 28px;
      height: 28px;
      background: rgba($primary-color, 0.1);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $primary-color;
      font-size: 13px;
    }
    
    .arrow-icon {
      margin-left: auto;
      font-size: 11px;
      color: $text-muted;
      transition: all 0.2s ease;
    }

    &:hover{
      background: rgba($primary-light, 0.3);
      
      .class-icon {
        background: $primary-light;
        color: white;
      }
      
      .arrow-icon {
        color: $primary-light;
      }
    }

    &.active{
      background: rgba($primary-color, 0.15);
      
      .class-icon {
        background: $primary-color;
        color: white;
      }

      span{
        color: $primary-dark;
        font-weight: 600;
      }
    }
  }

  .thinking-section{
    .section-header{
      margin-bottom: 20px;

      h4{
        font-size: 16px;
        font-weight: 600;
        color: $text-primary;
        margin: 0;
      }
    }

    .question-list{
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .question-item{
      background: $bg-secondary;
      border-radius: 10px;
      padding: 16px;
      display: flex;
      gap: 12px;

      .question-number{
        width: 28px;
        height: 28px;
        background: $primary-gradient;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 600;
        flex-shrink: 0;
      }

      .question-text{
        flex: 1;
        font-size: 14px;
        line-height: 1.7;
        color: $text-primary;

        .answer-area{
          margin-top: 12px;

          .answer-textarea{
            width: 100%;
            min-height: 80px;
            padding: 12px;
            border: 1px solid $border-color;
            border-radius: 8px;
            font-size: 13px;
            font-family: $font-family;
            resize: vertical;
            transition: all 0.2s ease;

            &:focus{
              outline: none;
              border-color: $primary-color;
              box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
            }
          }
        }
      }
    }
  }

  .choice-section{
    .section-header{
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      h4{
        font-size: 16px;
        font-weight: 600;
        color: $text-primary;
        margin: 0;
      }

      .question-count{
        font-size: 13px;
        color: $text-muted;
      }
    }

    .question-list{
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .question-item{
      background: $bg-secondary;
      border-radius: 10px;
      padding: 16px;
      display: flex;
      gap: 12px;

      .question-number{
        width: 28px;
        height: 28px;
        background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 600;
        flex-shrink: 0;
      }

      .question-content{
        flex: 1;

        .question-text{
          font-size: 14px;
          line-height: 1.7;
          color: $text-primary;
          margin-bottom: 14px;
          white-space: pre-wrap;
        }

        .options{
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .option{
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 14px;
          background: white;
          border: 1px solid $border-color;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;

          .option-label{
            width: 24px;
            height: 24px;
            background: $bg-secondary;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
            color: $text-secondary;
            flex-shrink: 0;
            transition: all 0.2s ease;
          }

          .option-text{
            flex: 1;
            font-size: 13px;
            color: $text-primary;
            line-height: 1.6;
            padding-top: 3px;
          }

          .correct-icon, .wrong-icon{
            font-size: 14px;
            flex-shrink: 0;
            padding-top: 3px;
          }

          .correct-icon{
            color: #10b981;
          }

          .wrong-icon{
            color: #ef4444;
          }

          &:hover{
            border-color: $primary-color;
            background: rgba($primary-color, 0.05);
          }

          &.selected{
            border-color: $primary-color;
            background: rgba($primary-color, 0.1);

            .option-label{
              background: $primary-color;
              color: white;
            }
          }

          &.correct{
            border-color: #10b981;
            background: rgba(16, 185, 129, 0.1);

            .option-label{
              background: #10b981;
              color: white;
            }
          }

          &.wrong{
            border-color: #ef4444;
            background: rgba(239, 68, 68, 0.1);

            .option-label{
              background: #ef4444;
              color: white;
            }
          }
        }
      }
    }

    .choice-actions{
      display: flex;
      gap: 12px;
      margin-top: 24px;
      justify-content: flex-end;

      .action-btn{
        padding: 10px 20px;
        background: $bg-secondary;
        border: none;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 500;
        color: $text-secondary;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover{
          background: darken($bg-secondary, 5%);
        }

        &.primary{
          background: $primary-gradient;
          color: white;

          &:hover{
            box-shadow: 0 4px 12px rgba($primary-color, 0.3);
          }
        }
      }
    }
  }

  .programming-section{
    height: 100%;
    display: flex;
    flex-direction: column;

    .section-header{
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      h4{
        font-size: 16px;
        font-weight: 600;
        color: $text-primary;
        margin: 0;
      }

      .header-actions{
        display: flex;
        gap: 8px;

        .action-btn{
          padding: 6px 14px;
          background: rgba($primary-color, 0.1);
          border: none;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;
          color: $primary-color;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;

          &:hover{
            background: rgba($primary-color, 0.2);
          }
        }
      }
    }

    .programming-content{
      flex: 1;
      display: flex;
      gap: 16px;
      overflow: hidden;

      .question-panel{
        width: 40%;
        background: $bg-secondary;
        border-radius: 10px;
        padding: 16px;
        overflow: auto;
        display: flex;
        flex-direction: column;

        .question-title{
          font-size: 14px;
          font-weight: 600;
          color: $primary-color;
          margin-bottom: 12px;
        }

        .question-body{
          flex: 1;
        }

        .programming-question{
          background: white;
          border-radius: 8px;
          padding: 14px;
          margin-bottom: 12px;
          cursor: pointer;
          border: 2px solid transparent;
          transition: all 0.2s ease;

          &:hover{
            border-color: rgba($primary-color, 0.3);
          }

          &.active{
            border-color: $primary-color;
            background: rgba($primary-color, 0.05);

            .question-label{
              color: $primary-color;
            }
          }

          .question-header{
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;

            .question-num{
              font-weight: 600;
              color: $primary-color;
            }

            .question-label{
              font-size: 13px;
              font-weight: 600;
              color: $text-primary;
            }
          }

          .question-desc{
            font-size: 13px;
            line-height: 1.7;
            color: $text-secondary;
            margin-bottom: 10px;
          }

          .question-example{
            background: $bg-dark;
            border-radius: 6px;
            padding: 10px 12px;

            .example-label{
              font-size: 11px;
              color: rgba(255, 255, 255, 0.7);
              margin-bottom: 6px;
            }

            .example-content{
              font-size: 12px;
              color: rgba(255, 255, 255, 0.9);
              font-family: $font-mono;
              white-space: pre-wrap;
            }
          }
        }
      }

      .editor-panel{
        flex: 1;
        display: flex;
        flex-direction: column;
        background: white;
        border-radius: 10px;
        border: 1px solid $border-color;
        overflow: hidden;

        .editor-header{
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 16px;
          background: $bg-secondary;
          border-bottom: 1px solid $border-color;

          span{
            font-size: 13px;
            font-weight: 500;
            color: $text-secondary;
          }

          .run-btn{
            padding: 6px 16px;
            background: $primary-gradient;
            border: none;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 500;
            color: white;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: all 0.2s ease;

            &:hover:not(:disabled){
              box-shadow: 0 4px 12px rgba($primary-color, 0.3);
            }

            &:disabled{
              opacity: 0.7;
              cursor: not-allowed;
            }
          }
        }

        .editor{
          flex: 1;
          border-radius: 0;
          border: none;

          &.editor-font-2{
            font-size: 14px !important;
          }

          .ace_print-margin{
            display: none;
          }
        }

        .stdin-input {
          position: relative;
          padding: 8px 16px;
          border-top: 1px solid $border-color;
          display: flex;
          align-items: center;
          gap: 8px;

          i {
            color: $text-muted;
            font-size: 12px;
          }

          input {
            flex: 1;
            border: none;
            outline: none;
            font-size: 13px;
            font-family: $font-mono;

            &::placeholder {
              color: $text-muted;
            }
          }
        }

        .result-panel {
          background: $bg-dark;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          max-height: 120px;
          display: flex;
          flex-direction: column;

          .result-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 16px;
            background: rgba(255, 255, 255, 0.05);
            color: white;
            font-size: 12px;
            font-weight: 500;

            .close-btn {
              background: none;
              border: none;
              color: rgba(255, 255, 255, 0.6);
              cursor: pointer;
              padding: 2px;
              transition: color 0.2s;

              &:hover {
                color: white;
              }
            }
          }

          .result-content {
            padding: 12px 16px;
            color: rgba(255, 255, 255, 0.9);
            font-family: $font-mono;
            font-size: 12px;
            line-height: 1.6;
            overflow: auto;
          }
        }
      }
    }
  }

  .ace_gutter-cell.error-line{
    background:orangered;
    color:white;
  }

  .ace_gutter-cell .ace_fold-widget{
    display: none !important
  }

  .ace_identifier{
    color:rgb(189, 60, 1);
  }

  .ace_numeric{
    color:#038795 !important;
  }

  .ace_keyword{
    color:rgb(43, 90, 1) !important;
  }
  </style>
